import React, { useState, useEffect } from 'react';
import logo from "../assets/logo.webp"
import { Link } from 'react-router-dom';
import { User, Menu, X, Home, FileText, CreditCard, LogOut, LogIn, Upload, Download, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const Header = () => {
  const { isAuthenticated, logout } = useAuth();
  const { showToast } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token || !isAuthenticated) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/studentcheck`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
        });
        const data = await response.json();
        
        if (data.logged_out === true) {
          showToast(data.message || 'Your session has ended', 'error');
          logout();
        }
      } catch (error) {
        console.error('Error checking student status:', error);
      }
    };

    if (isAuthenticated) {
      checkStatus();
    }
  }, [isAuthenticated, logout]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleLogout = () => {
    logout();
    setIsSidebarOpen(false);
  };

  const menuItems = isAuthenticated ? [
    { icon: Home, label: 'Home', path: '/' },
    { icon: User, label: 'Dashboard', path: '/dashboard' },
    { icon: User, label: 'My Profile', path: '/profile' },
    { icon: FileText, label: 'My Application', path: '/application' },
    { icon: Upload, label: 'Upload Documents', path: '/documents' },
    { icon: Calendar, label: 'Session Details', path: '/session' },
    { icon: CreditCard, label: 'Payment', path: '/payment' },
    { icon: Download, label: 'Download ID Card', path: '/idcard' },
    { icon: LogOut, label: 'Logout', path: '#', onClick: handleLogout },
  ] : [
    { icon: Home, label: 'Home', path: '/' },
    { icon: LogIn, label: 'Login', path: '/login' },
    { icon: User, label: 'Register', path: '/register' },
  ];

  return (
    <>
      <header className="pool-header">
        <div className="container-fluid">
          <div className="pool-header-inner">
            <div className="pool-brand">
              <Link to="/">
                <div className="pool-logo">
                  <img src={logo} alt="Balurghat Municipality Swimming Pool Logo" />
                </div>
              </Link>

              <div className="pool-brand-text">
                Balurghat Municipality<br />Swimming Pool
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="pool-nav desktop-nav">
              {isAuthenticated ? (
                <Link to="/dashboard" className="pool-nav-link d-flex align-items-center gap-2">
                  <User size={20} />
                  Dashboard
                </Link>
              ) : (
                <div className="d-flex gap-2">
                  <Link to="/login" className="pool-nav-link d-flex align-items-center gap-2">
                    <LogIn size={20} />
                    Login
                  </Link>
                  <Link to="/register" className="pool-nav-link d-flex align-items-center gap-2">
                    <User size={20} />
                    Register
                  </Link>
                </div>
              )}
            </nav>

            {/* Mobile Hamburger Menu */}
            <button 
              className="hamburger-menu mobile-nav"
              onClick={toggleSidebar}
              aria-label="Toggle menu"
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      <div className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`} onClick={toggleSidebar}>
        <div className={`sidebar ${isSidebarOpen ? 'active' : ''}`} onClick={(e) => e.stopPropagation()}>
          <div className="sidebar-header">
            <div className="sidebar-brand">
              <div className="sidebar-logo">
                <img src={logo} alt="Balurghat Municipality Swimming Pool Logo" />
              </div>
              <div className="sidebar-brand-text">
                Balurghat<br />Swimming Pool
              </div>
            </div>
            <button className="sidebar-close" onClick={toggleSidebar}>
              <X size={20} />
            </button>
          </div>
          
          <nav className="sidebar-nav">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <Link
                  key={index}
                  to={item.path}
                  className="sidebar-item"
                  onClick={item.onClick || (() => setIsSidebarOpen(false))}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;
